import { Component, Input } from "@angular/core";

import { Calculation, CalculatorLogic, Avatar } from "@salaxy/core";

import { SalaxyContext } from "../../../services/index";

/** Provides a standard user interface to edit a worker object. */
@Component({
    selector: "salaxy-calc-list",
    template: `<mat-list>
    <h3 md-subheader style="max-width: 350px">
        <button md-mini-fab style="float: right" (click)="createNew()"><mat-icon>add</mat-icon></button>
        Palkkalaskelmat
    </h3>
    <mat-list-item *ngFor="let calc of list" (click)="setCurrentId(calc.id)" class="click">
        <salaxy-avatar [avatar]="getOtherParty(calc)"
                style="font-size: 48px;" md-list-avatar></salaxy-avatar>
            <h4 md-line>{{ calc.result.totals.totalGrossSalary | currency : 'EUR' : true }} at {{calc.paidAt | date}}</h4>
            <p md-line>{{ getOtherParty(calc).displayName }}</p>
            <p md-line>{{ calc.id }}</p>
    </mat-list-item>
</mat-list>
`,
})
export class NgCalcListComponent {
    /**
     * Property based on which the groupedList is grouped.
     */
    @Input()
    public groupBy: "employer" | "worker" | "otherParty" | "year" | "month" | "status" = "status";

    /** Limit number of items to show in the list */
    @Input()
    public limitTo: number;

    /**
     * A comma separated list of statuses that are used as filter for the list.
     * In JavaScript
     */
    @Input()
    public filterStatuses: string | string[];

    constructor(protected context: SalaxyContext) {
    }

    /**
     * Gets the Avatar of other party in a calculation:
     * Employer or Worker
     */
    public getOtherParty = (calc: Calculation) => CalculatorLogic.getOtherParty(calc, this.context.session.avatar);

    /** The item that is currently in edit / detail view */
    public get current(): Calculation {
        return this.context.calculations.current;
    }

    /**
     * Delete the item from repository.
     * Note that this may not always be possible. At least check model.isReadOnly
     * @param id - Identifier for the item to be deleted.
     */
    public delete(id: string) {
        this.context.calculations.delete(id);
    }

    /**
     * List grouped based on groupBy value
     */
    public get groupedList() {
        const sortDescending = this.groupBy === "year" || this.groupBy === "month" ? true : false;
        const groupedObj = this.list.reduce((prev, cur) => {
            // Worker as the default
            let key = "Unknown grouping: " + (this.groupBy || "[null]");
            switch (this.groupBy) {
                case "employer":
                    key = cur.employer.avatar.sortableName || "Työnantajaa ei valittu";
                    break;
                case "worker":
                    key = cur.worker.avatar.sortableName || "Työntekijää ei valittu";
                    break;
                case "otherParty":
                    key = this.getOtherParty(cur).sortableName;
                    break;
                case "year":
                    {
                        // TODO: Move the getting of a date to a generic helper
                        const date: any = cur.workflow.paidAt || cur.updatedAt || cur.createdAt || "0000";
                        if (date.getFullYear) {
                            key = "" + date.getFullYear();
                        } else {
                            key = ("" + date).substr(0, 4);
                        }
                        break;
                    }
                case "month":
                    {
                        // TODO: Move the getting of a date to a generic helper
                        const date: any = cur.workflow.paidAt || cur.updatedAt || cur.createdAt || "0000";
                        if (date.getFullYear) {
                            key = "" + date.getFullYear() + "-" + (cur.workflow.paidAt.getMonth() + 1);
                        } else {
                            // ISO DATE yyyy-MM
                            key = ("" + date).substr(0, 7);
                        }
                        break;
                    }
                case "status":
                    key = cur.workflow.status as any;
                    break;
            }

            if (!prev[key]) {
                prev[key] = [cur];
            } else {
                prev[key].push(cur);
            }
            return prev;
        }, {});
        return Object.keys(groupedObj)
            .map((key) => ({ key, value: groupedObj[key] }))
            .sort((n1, n2) => {
                if (n1.key > n2.key) {
                    return sortDescending ? -1 : 1;
                }
                if (n1.key < n2.key) {
                    return sortDescending ? 1 : -1;
                }
                return 0;
            });
    }

    /**
     * List of calculations
     * The list is filtered if a filter is set
     */
    public get list() {
        let result = this.context.calculations.list;
        if (this.filterStatuses) {
            let statusArray: string[];
            if (Array.isArray(this.filterStatuses)) {
                statusArray = this.filterStatuses;
            } else {
                statusArray = this.filterStatuses.split(",");
            }
            if (statusArray.length === 0 || !statusArray[0]) {
                result = this.context.calculations.list;
            } else {
                result = this.context.calculations.list.filter((item) => {
                    return statusArray.some((statusValue) => item.workflow.status as any === statusValue);
                });
            }
        }
        if (this.limitTo) {
            return result.slice(0, this.limitTo);
        }
        return result;
    }

    /** Sets the current ID for the calculation */
    public setCurrentId(id: string) {
        this.context.calculations.setCurrentId(id);
    }

    /** Creates a new calculation */
    public createNew() {
        this.context.calculations.newCurrent();
    }

}
